import React, { useMemo, useRef, useState } from "react";
import {Button, Col, DatePicker, Row, Switch, Table, Tabs} from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
// import { useReactToPrint } from "react-to-print";
import { CSVLink } from "react-csv";
import dayjs from "dayjs";

import {
  DATE_FORMAT,
  buildPointes,
  flattenSelectedLignes,
  parseFrenchDate,
} from "./pointe";

const { RangePicker } = DatePicker;

const TablePointe = ({ comptes = [], ecritures = [], parametre }) => {
  const [dates, setDates] = useState([dayjs().startOf("year"), dayjs()]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pointes, setPointes] = useState([]);
  const [dataChart, setDataChart] = useState([]);
  const [soldeInitial, setSoldeInitial] = useState(0);
  const [compensationProprietaire, setCompensationProprietaire] = useState(true);
  const componentRef = useRef(null);

  const onChangeRange = (values) => {
    if (!values || !values[0] || !values[1]) {
      setDates([dayjs().startOf("year"), dayjs()]);
      return;
    }

    setDates([values[0], values[1]]);
  };

  const preparePointe = () => {
    try {
      const lignesEcritures = flattenSelectedLignes(
        ecritures,
        selectedRowKeys,
        comptes
      );

      const result = buildPointes({
        lignesEcritures,
        dateDebut: dates[0],
        dateFin: dates[1],
        compensationProprietaire,
      });

      setSoldeInitial(result.soldeInitial);
      setPointes(result.pointes);
      setDataChart(result.dataChart);
    } catch (error) {
      console.error(error);
    }
  };

  /*const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });*/

  const pointeMax = useMemo(() => {
    if (!pointes.length) return null;
    return pointes.reduce((max, row) => (row.solde > max.solde ? row : max), pointes[0]);
  }, [pointes]);

  const csvData = useMemo(
    () =>
      pointes.map((row) => ({
        date: row.ecriture_date_ecriture,
        mouvement_jour: row.mouvementJour,
        solde_cumule: row.solde,
        nb_lignes: row.lignesCount,
      })),
    [pointes]
  );

  const rowSelection = {
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
    selectedRowKeys,
    selectedRows,
  };

  const getColor = (row) => {
    if (row.solde > parametre?.parametre_pointe_max) return "pointe-bg-red";
    return "";
  };

  const comptesColumns = [
    {
      title: "Code",
      key: "compte_compte_general",
      render: (record) => record.compte_compte_general,
      sorter: (a, b) =>
        String(a.compte_compte_general).localeCompare(String(b.compte_compte_general)),
      defaultSortOrder: "ascend",
    },
    {
      title: "Libellé",
      key: "compte_libelle",
      render: (record) => record.compte_libelle,
    },
  ];

  const pointeColumns = [
    {
      title: "Date",
      key: "ecriture_date_ecriture",
      render: (record) =>
        parseFrenchDate(record.ecriture_date_ecriture).isValid()
          ? parseFrenchDate(record.ecriture_date_ecriture).format(DATE_FORMAT)
          : record.ecriture_date_ecriture,
    },
    {
      title: "Mouvement jour",
      key: "mouvementJour",
      align: "right",
      render: (record) =>
        Number(record.mouvementJour).toLocaleString("fr-FR", {
          style: "currency",
          currency: "EUR",
        }),
    },
    {
      title: "Solde cumulé",
      key: "solde",
      align: "right",
      render: (record) =>
        Number(record.solde).toLocaleString("fr-FR", {
          style: "currency",
          currency: "EUR",
        }),
    },
    {
      title: "Nb lignes",
      key: "lignesCount",
      align: "right",
      render: (record) => record.lignesCount,
    },
  ];

  return (
    <>
      <RangePicker
        value={dates}
        format={DATE_FORMAT}
        onChange={onChangeRange}
      />

      <Table
        columns={comptesColumns}
        dataSource={comptes}
        pagination={false}
        rowKey="compte_compte_general"
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        scroll={{ y: 252 }}
        size="small"
        showSorterTooltip={false}
        title={() => "Sélection des comptes"}
      />

      <Row style={{ marginTop: 20, width: "100%" }}>
        <Col span={24} style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>

          <Switch
            checked={compensationProprietaire}
            onChange={setCompensationProprietaire}
            checkedChildren="Compensation"
            unCheckedChildren="Non-compensation"
          />
          <Button type="primary" onClick={preparePointe}>
            Calcul pointe
          </Button>

          <Button>
            <CSVLink filename="export.csv" data={csvData} separator=";">
              Export CSV
            </CSVLink>
          </Button>

          {/*<Button type="primary" onClick={handlePrint}>
            Imprimer
          </Button>*/}
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={24}>
          <Tabs
            defaultActiveKey="1"
            tabPosition="left"
            items={[
              {
                label: "Table",
                key: "1",
                children: (
                  <>
                    <div style={{ marginBottom: 12, fontStyle: "italic" }}>
                      Solde initial :
                      {" "}
                      {Number(soldeInitial).toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </div>

                    <Table
                      columns={pointeColumns}
                      dataSource={pointes}
                      pagination={false}
                      rowKey="key"
                      scroll={{ y: 252 }}
                      size="small"
                      rowClassName={getColor}
                      showSorterTooltip={false}
                    />
                  </>
                ),
              },
              {
                label: "Graph",
                key: "2",
                style: { width: "100%", height: "100%" },
                children: (
                  <div ref={componentRef}>
                    <LineChart
                      width={800}
                      height={400}
                      data={dataChart}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <ReferenceLine
                        y={parametre?.parametre_pointe_max}
                        label={`Pointe max autorisée (${Number(parametre?.parametre_pointe_max).toLocaleString("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                        })})`}
                        stroke="red"
                        strokeDasharray="3 3"
                      />
                      <Line
                        type="monotone"
                        dataKey="pv"
                        name="Pointe"
                        stroke="#8884d8"
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>

                    <br />

                    {pointeMax && (
                      <span style={{ marginLeft: 15, fontStyle: "italic" }}>
                        * Pointe max atteinte le {pointeMax.ecriture_date_ecriture} pour un montant de{" "}
                        {Number(pointeMax.solde).toLocaleString("fr-FR", {
                          style: "currency",
                          currency: "EUR",
                        })}
                      </span>
                    )}
                    { parametre?.parametre_pointe_max && (
                      <span style={{ marginLeft: 15, fontStyle: "italic", fontWeight: "bold" }}>
                        Pointe max autorisée ({Number(parametre?.parametre_pointe_max).toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "EUR",
                      })})
                      </span>
                    ) }
                  </div>
                ),
              },
            ]}
            style={{ marginTop: 20 }}
          />
        </Col>
      </Row>
    </>
  );
};

export default TablePointe;